/**
 * Game Socket Hook
 * Manages Socket.io connection and game events with reconnection handling
 */

import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useGameStore } from '../store/gameStore';
import { getSocketUrl } from '../config/app.config';
import { showToast } from '../lib/toast';
import { logger } from '../lib/logger';

interface UseGameSocketProps {
  tableId: string;
  token: string;
  /** Set false to skip connecting entirely — for mock/offline views driven by local store state only. Defaults to true. */
  enabled?: boolean;
}

export const useGameSocket = ({ tableId, token, enabled = true }: UseGameSocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const setGameState = useGameStore((state) => state.setGameState);
  const updatePlayer = useGameStore((state) => state.updatePlayer);
  const updateCommunityCard = useGameStore((state) => state.updateCommunityCard);
  const addChatMessage = useGameStore((state) => state.addChatMessage);
  const setHandResult = useGameStore((state) => state.setHandResult);
  const addActionHistory = useGameStore((state) => state.addActionHistory);
  const applyOptimisticAction = useGameStore((state) => state.applyOptimisticAction);
  const confirmOptimisticAction = useGameStore((state) => state.confirmOptimisticAction);
  const rollbackOptimisticAction = useGameStore((state) => state.rollbackOptimisticAction);

  // Initialize socket connection
  useEffect(() => {
    if (!enabled) return;

    const socket = io(`${getSocketUrl()}/game`, {
      // tableId lets the server resolve the right session when this user
      // has more than one active one (e.g. a leftover seed-data table
      // alongside their own solo table) instead of picking one arbitrarily.
      auth: { token, tableId },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Keep trying for as long as the page is open — a server restart can
      // easily outlast a handful of attempts, and giving up leaves the table
      // dead until a manual refresh.
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    // Connection established
    socket.on('connect', () => {
      logger.socket('connected', { tableId });
      setGameState({
        isConnected: true,
        connectionStatus: 'connected',
        reconnectAttempt: 0,
      });
    });

    // Set when the server refuses our token — reconnecting with the same
    // token can only fail again, so every reconnect path checks this first.
    let authFailed = false;

    // Connection lost
    socket.on('disconnect', (reason) => {
      logger.socket('disconnected', { tableId, reason });
      if (authFailed) return; // 'failed' already set by the AUTH_FAILED handler
      // We closed it ourselves (leaving the page or the table): nothing was lost.
      if (reason === 'io client disconnect') return;

      setGameState({
        isConnected: false,
        connectionStatus: 'disconnected',
      });

      if (reason === 'io server disconnect') {
        // Server forcibly closed connection; reconnect manually
        logger.warn('Server disconnected socket, attempting manual reconnect', { tableId });
        socket.connect();
      }
    });

    // Reconnection lifecycle events belong to the Manager (socket.io), not
    // the Socket, in socket.io-client v4 — registered on the socket they
    // never fire.
    socket.io.on('reconnect_attempt', (attempt) => {
      logger.socket('reconnect_attempt', { tableId, attempt });
      setGameState({
        connectionStatus: 'reconnecting',
        reconnectAttempt: attempt,
      });
    });

    // Successfully reconnected
    socket.io.on('reconnect', (attempt) => {
      logger.socket('reconnected', { tableId, attempts: attempt });
      setGameState({
        isConnected: true,
        connectionStatus: 'connected',
        reconnectAttempt: 0,
      });
      showToast('Reconnected to game', 'success');

      // Request fresh state after reconnection
      socket.emit('request-state', { tableId }, (response: any) => {
        if (response?.success) {
          logger.info('State resynced after reconnection', { tableId });
        } else {
          logger.warn('State resync failed after reconnection', { tableId, response });
        }
      });
    });

    // Reconnection failed
    socket.io.on('reconnect_failed', () => {
      logger.error('Reconnection failed after max attempts', undefined, { tableId });
      setGameState({
        connectionStatus: 'failed',
      });
      showToast('Connection lost. Please refresh the page.', 'error');
    });

    // Connection errors
    socket.on('connect_error', (err) => {
      logger.error('Socket connection error', err, { tableId });
      setGameState({ connectionStatus: 'reconnecting' });
    });

    socket.on('error', (data: { code: string; message: string }) => {
      logger.error('Socket error received', undefined, { tableId, code: data.code, message: data.message });

      if (data.code === 'AUTH_FAILED') {
        // Expired/invalid token or a banned account: stop, don't loop.
        authFailed = true;
        socket.io.opts.reconnection = false;
        setGameState({ isConnected: false, connectionStatus: 'failed' });
        showToast('Your session has expired. Please log in again.', 'error');
        return;
      }

      showToast(data.message || 'Game error occurred', 'error');
    });

    // Game state event
    socket.on('game-state', (data: any) => {
      logger.socket('game-state', { tableId, playerCount: data.players?.length });

      if (!data.table) {
        // Initial connection ack before joining any table — nothing to apply yet.
        return;
      }

      const currentPlayerId = data.currentHand?.currentPlayerId ?? null;
      // The chip holder opens every betting round of this hand.
      const chipPlayerId = data.currentHand?.chipPlayerId ?? null;
      const handDescById = new Map(
        (data.currentHand?.showdownHands || []).map((h: any) => [h.playerId, h.handDescription])
      );
      const jewelHandDescById = new Map(
        (data.currentHand?.showdownJewelHands || []).map((h: any) => [
          h.playerId,
          h.jewelHandDescription,
        ])
      );

      setGameState({
        tableId: data.table.id,
        // Server field is `cards` (see GameStatePayload); the store's own
        // field is `holeCards` — own cards real, opponents'
        // hidden/masked, see ViewerFacingCard. isCurrentTurn wasn't set at
        // all here before — this is the only state source on a fresh
        // connect/reconnect, so without it nobody's action buttons would
        // ever appear until the next phase change (if even then — see the
        // phase-changed and action-broadcast handlers below).
        players: (data.players || []).map((p: any) => ({
          ...p,
          holeCards: p.cards,
          isCurrentTurn: p.id === currentPlayerId,
          hasDealer: p.id === chipPlayerId,
          handDescription: handDescById.get(p.id),
          jewelHandDescription: jewelHandDescById.get(p.id),
        })),
        communityCards: data.currentHand?.communityCards || [],
        pot: data.currentHand?.pot || 0,
        sidePots: data.currentHand?.sidePots || [],
        currentBet: data.currentHand?.currentBet || 0,
        minRaise: data.currentHand?.minRaise ?? null,
        phase: data.currentHand?.phase || 'ANTE',
        // Not seated (watching a table before joining): no seat, no chips.
        yourSeat: data.yourSeat ?? null,
        yourChips: data.yourChips ?? 0,
        tableOver: data.tableOver ?? null,
        ante: data.ante ?? null,
        specialCardEffects: data.specialCardEffects ?? false,
        knockedOutPlace: data.you?.knockedOutPlace ?? null,
        spectating: data.you?.spectating ?? false,
        awaitingEndState: false,
      });

      // A (re)connect after a game ended: restore its pot-by-pot result,
      // which otherwise only arrives with the 'hand-ended' event.
      const pots = data.currentHand?.pots;
      if (data.currentHand?.phase === 'ENDED' && Array.isArray(pots) && pots.length > 0 && !useGameStore.getState().handResult) {
        setHandResult({
          winners: pots.flatMap((p: any) => p.rankWinners),
          jewelWinners: pots.flatMap((p: any) =>
            p.jewelWinners.map((w: any) => ({ ...w, jewelHandDescription: w.handDescription }))
          ),
          pot: pots.reduce((sum: number, p: any) => sum + p.amount, 0),
          pots,
          timestamp: Date.now(),
        });
      }
    });

    // Player joined event
    socket.on('player-joined', (data: any) => {
      console.log('👤 Player joined:', data.player.username);
      setGameState((state: any) => ({
        players: [...state.players, {
          ...data.player,
          seatPosition: data.seatNumber,
          status: 'ACTIVE',
          isConnected: true,
        }],
      }));
    });

    // Player left event
    socket.on('player-left', (data: { userId: string; tableId: string }) => {
      console.log('👤 Player left:', data.userId);
      setGameState((state: any) => ({
        players: state.players.filter((p: any) => p.id !== data.userId),
      }));
    });

    // Card revealed event
    socket.on('card-revealed', (data: any) => {
      console.log('🃏 Card revealed:', data);
      // Animation replay of a phase change the store already applied — if
      // it belongs to a different phase (e.g. a late one from the last
      // game, after the next was dealt), it must not touch this grid.
      if (data.phase && data.phase !== useGameStore.getState().phase) return;
      updateCommunityCard(data.position, {
        position: data.position,
        card: data.card,
        revealed: true,
      });
    });

    // Your own cards, privately: in a run-out (everyone all-in) the specials
    // turn over after the corners like in any hand, but the hand is already
    // over on the server, so this comes on its own rather than as a game-state.
    socket.on('own-cards', (data: { tableId: string; cards: any[] }) => {
      const state = useGameStore.getState();
      if (data.tableId !== state.tableId || state.yourSeat === null) return;
      const you = state.players.find((p) => p.seatPosition === state.yourSeat);
      if (you) updatePlayer(you.id, { holeCards: data.cards });
    });

    // Action broadcast event
    socket.on('action-broadcast', (data: any) => {
      logger.socket('action-broadcast', {
        tableId,
        userId: data.userId,
        action: data.action,
        amount: data.amount
      });

      // Check if this confirms a pending optimistic action
      const currentPendingAction = useGameStore.getState().pendingAction;
      if (currentPendingAction && currentPendingAction.userId === data.userId) {
        logger.debug('Server confirmed optimistic action', {
          action: data.action,
          userId: data.userId
        });
        confirmOptimisticAction();
      }

      // Apply server state (source of truth). playerCurrentBet is their
      // total put in this round; `amount` is only what this one action added.
      updatePlayer(data.userId, {
        currentBet: data.playerCurrentBet ?? 0,
        chipCount: data.currentChips,
      });

      // Update status based on action — any bet that used the last chip is an all-in
      if (data.action === 'FOLD') {
        updatePlayer(data.userId, { status: 'FOLDED' });
      } else if (data.action === 'ALL_IN' || data.currentChips === 0) {
        updatePlayer(data.userId, { status: 'ALL_IN' });
      }

      // Whose turn it is now, and the table's new pot/bet level — bundled
      // directly in this same event (not just the separate 'pot-updated'
      // broadcast) so a client that misses that other event doesn't act on
      // a stale currentBet (e.g. showing "Check" and sending CHECK when
      // there's actually a bet to call, which the engine rejects).
      setGameState((state: any) => ({
        players: state.players.map((p: any) => ({
          ...p,
          isCurrentTurn: p.id === data.nextPlayerId,
        })),
        pot: data.pot,
        currentBet: data.currentBet,
        minRaise: data.minRaise ?? null,
        sidePots: data.sidePots || [],
      }));

      // Add to action history if not already added optimistically
      if (!currentPendingAction || currentPendingAction.userId !== data.userId) {
        addActionHistory({
          id: `${data.userId}-${Date.now()}`,
          userId: data.userId,
          username: data.username || 'Unknown',
          action: data.action,
          amount: data.amount,
          timestamp: Date.now(),
        });
      }
    });

    // Pot updated event
    socket.on('pot-updated', (data: any) => {
      console.log('💰 Pot updated:', data.pot);
      setGameState({
        pot: data.pot,
        sidePots: data.sidePots || [],
        currentBet: data.currentBet,
      });
    });

    // Phase changed event
    socket.on('phase-changed', (data: any) => {
      console.log('🔄 Phase changed:', data.phase, 'revealedCards:', JSON.stringify(data.revealedCards));
      setGameState({
        phase: data.phase,
        ...(data.phase === 'ENDED' ? { awaitingEndState: true } : {}),
      });

      // Reset current turn — server field is nextPlayerId, not nextPlayer
      // (this was reading the wrong key, so isCurrentTurn silently landed
      // on nobody after every single phase change).
      // New betting round: everyone's contribution for the round starts at 0.
      setGameState((state: any) => ({
        players: state.players.map((p: any) => ({
          ...p,
          isCurrentTurn: p.id === data.nextPlayerId,
          currentBet: 0,
        })),
      }));

      // Apply every newly-revealed card right here rather than relying
      // solely on the separate, staggered 'card-revealed' events the
      // gateway also sends (500ms apart, for the reveal animation) — a
      // client that reconnects mid-stagger (e.g. after a brief network
      // blip) misses whichever of those hadn't fired yet, permanently
      // leaving those cells face-down even though the phase itself keeps
      // advancing correctly. This makes the actual game state land
      // atomically with the phase change; the later 'card-revealed'
      // events just replay the same (idempotent) update for the animation.
      if (Array.isArray(data.revealedCards)) {
        for (const { position, card } of data.revealedCards) {
          updateCommunityCard(position, { position, card, revealed: true });
        }
      }
    });

    // The table's game is over: last player standing, or you're out
    socket.on('table-over', (data: { winner: { userId: string; username: string } | null; standings: any[] }) => {
      setGameState({ tableOver: { winner: data.winner, standings: data.standings ?? [] } });
    });

    // Server-run turn countdown (it auto-checks, else folds, at the deadline)
    // The countdown to the next hand dealing itself.
    socket.on('deal-timer', (data: { tableId: string; deadline: number | null }) => {
      setGameState({ dealDeadline: data.deadline });
    });

    socket.on('turn-timer', (data: { playerId: string | null; deadline: number | null }) => {
      setGameState({ turnPlayerId: data.playerId, turnDeadline: data.deadline });
    });

    // Hand ended event
    socket.on('hand-ended', (data: any) => {
      console.log('🏁 Hand ended:', data);
      setGameState({
        phase: 'ENDED',
        awaitingEndState: true,
      });

      // Record winners for in-place highlighting on each player's own row
      // (OpponentList / GameTable) — no toast, no popup. HandEndedPayload
      // has no `pot` field — the total is the sum of both axes' distributions.
      const winners = data.winners || [];
      const jewelWinners = data.jewelWinners || [];
      if (winners.length > 0 || jewelWinners.length > 0) {
        const pot = Object.values(data.distribution || {}).reduce(
          (sum: number, amount: any) => sum + (amount as number),
          0
        );

        setHandResult({
          winners,
          jewelWinners,
          pot,
          pots: data.pots || [],
          timestamp: Date.now(),
        });
      }
    });

    // Player presence event
    socket.on('player-presence', (data: any) => {
      updatePlayer(data.userId, {
        isConnected: data.isConnected,
        status: data.isConnected ? 'ACTIVE' : 'DISCONNECTED',
      });
    });

    // Chat message event
    socket.on('chat-message', (data: any) => {
      addChatMessage({
        id: `${data.userId}-${Date.now()}`,
        userId: data.userId,
        username: data.username,
        message: data.message,
        timestamp: data.timestamp,
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [
    enabled,
    token,
    tableId,
    setGameState,
    updatePlayer,
    updateCommunityCard,
    addChatMessage,
    setHandResult,
    addActionHistory,
    confirmOptimisticAction,
  ]);

  // Join table
  const joinTable = useCallback(
    (buyInAmount: number) => {
      return new Promise((resolve, reject) => {
        socketRef.current?.emit('join-table', { tableId, buyInAmount }, (response: any) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.error || 'Failed to join table'));
          }
        });
      });
    },
    [tableId]
  );

  // Leave table
  const leaveTable = useCallback(() => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('leave-table', { tableId }, (response: any) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || 'Failed to leave table'));
        }
      });
    });
  }, [tableId]);

  // Perform action with optimistic UI update
  const performAction = useCallback(
    (action: string, amount?: number, userId?: string) => {
      return new Promise((resolve, reject) => {
        // Get user ID from parameters or current game state
        const currentUserId = userId || useGameStore.getState().players.find(
          p => p.seatPosition === useGameStore.getState().yourSeat
        )?.id;

        if (!currentUserId) {
          logger.warn('Cannot apply optimistic action: user ID not found');
          reject(new Error('User ID not found'));
          return;
        }

        // Apply optimistic update immediately
        logger.debug('Applying optimistic action', {
          action,
          amount,
          userId: currentUserId
        });
        applyOptimisticAction(currentUserId, action, amount);

        // Emit action to server
        socketRef.current?.emit(
          'player-action',
          {
            tableId,
            action,
            amount,
            timestamp: Date.now(),
          },
          (response: any) => {
            if (response.success) {
              logger.debug('Server confirmed action', { action, userId: currentUserId });
              // confirmOptimisticAction is called in action-broadcast handler
              resolve(response);
            } else {
              logger.warn('Server rejected action, rolling back', {
                action,
                error: response.error,
                userId: currentUserId
              });
              // Rollback optimistic update
              rollbackOptimisticAction();
              // Show error to user
              showToast(response.error || 'Action failed', 'error');
              reject(new Error(response.error || 'Action failed'));
            }
          }
        );

        // Set timeout for rollback if no response
        setTimeout(() => {
          const stillPending = useGameStore.getState().pendingAction;
          if (stillPending && stillPending.userId === currentUserId) {
            logger.error('Action timeout, rolling back optimistic update', {
              action,
              userId: currentUserId
            });
            rollbackOptimisticAction();
            showToast('Action timed out. Please try again.', 'error');
            reject(new Error('Action timeout'));
          }
        }, 5000); // 5 second timeout
      });
    },
    [tableId, applyOptimisticAction, rollbackOptimisticAction]
  );

  // Send chat message
  const sendChatMessage = useCallback(
    (message: string) => {
      socketRef.current?.emit('chat-message', {
        tableId,
        message,
      });
    },
    [tableId]
  );

  // Start the next hand — manual, triggered by the "Deal New Game" button
  // shown in place of the betting controls once a hand ends. Nothing
  // auto-starts the next hand server-side anymore.
  /** Out of chips: keep watching the game instead of leaving. */
  const spectate = useCallback(() => {
    return new Promise((resolve, reject) => {
      const fail = (message: string) => {
        showToast(message, 'error');
        reject(new Error(message));
      };
      const socket = socketRef.current;
      if (!socket?.connected) {
        fail('Not connected to the game server — reconnecting…');
        return;
      }
      socket.timeout(10_000).emit('spectate', { tableId }, (err: Error | null, response: any) => {
        if (err) fail('The server did not respond — please try again.');
        else if (response?.success) resolve(response);
        else fail(response?.error || 'Could not spectate');
      });
    });
  }, [tableId]);

  const startNextHand = useCallback(() => {
    return new Promise((resolve, reject) => {
      const fail = (message: string) => {
        showToast(message, 'error');
        reject(new Error(message));
      };

      const socket = socketRef.current;
      if (!socket?.connected) {
        // An emit while disconnected is buffered and may never be answered,
        // which would leave the "Dealing…" button spinning forever.
        fail('Not connected to the game server — reconnecting…');
        return;
      }

      socket.timeout(10_000).emit('start-next-hand', { tableId }, (err: Error | null, response: any) => {
        if (err) {
          fail('The server did not respond — please try again.');
        } else if (response?.success) {
          resolve(response);
        } else {
          fail(response?.error || 'Failed to start next hand');
        }
      });
    });
  }, [tableId]);

  // Request current state
  const requestState = useCallback(() => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('request-state', { tableId }, (response: any) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || 'Failed to get state'));
        }
      });
    });
  }, [tableId]);

  return {
    socket: socketRef.current,
    joinTable,
    leaveTable,
    performAction,
    sendChatMessage,
    requestState,
    startNextHand,
    spectate,
  };
};
